import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Search, Loader2, Settings2, Eye, AlertTriangle, History, FilePlus2, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, useAuth, isAdmin } from "@/context/AuthContext";
import { PIInvoicePicker } from "@/components/proforma/PIInvoicePicker";
import { PILines } from "@/components/proforma/PILines";
import { PIAdjustments } from "@/components/proforma/PIAdjustments";
import { PIHeader } from "@/components/proforma/PIHeader";
import { PIHistory } from "@/components/proforma/PIHistory";
import { PISettingsDialog } from "@/components/proforma/PISettingsDialog";
import { PIPreviewDialog } from "@/components/proforma/PIPreviewDialog";
import { inr, r2, dmy, amountInWords, apportionAdvance, autoAdjustments, onAccountFor, gstByRate, downloadPdf, downloadXlsx, downloadDraft } from "@/lib/proforma";

const today = () => new Date().toISOString().slice(0, 10);

export default function ProformaInvoice() {
  const { user } = useAuth();
  const [tab, setTab] = useState("new");
  const [settings, setSettings] = useState(null);
  const [mode, setMode] = useState("project");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [cust, setCust] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [descs, setDescs] = useState({});
  const [pi, setPi] = useState({ date: today() });
  const [adjs, setAdjs] = useState([]);
  const [scope, setScope] = useState("project");
  const [loaded, setLoaded] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  const loadSettings = (date) => axios.get(`${API}/proforma/settings`, { params: { date } }).then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { loadSettings(pi.date); }, [pi.date]);

  const custInvoices = useMemo(() => (res?.invoices || []).filter((i) => i.customer === cust), [res, cust]);
  const selInvoices = useMemo(() => custInvoices.filter((i) => selected.has(i.billing_doc)), [custInvoices, selected]);
  const customer = (res?.customers || []).find((c) => c.code === cust);
  const project = res?.mode === "project" ? res.query : (selInvoices[0]?.lines[0]?.wbs || "").slice(0, 4);

  const lines = useMemo(() => (loaded ? loaded.lines : selInvoices.flatMap((i) => i.lines.map((l) => ({
    invoice_no: i.billing_doc, date: i.date, description: descs[`${i.billing_doc}-${l.item}`] ?? l.description, qty: l.qty, unit: l.unit,
    rate: l.rate, amount: l.amount, gst_rate: l.gst_rate, tax: l.tax, _key: `${i.billing_doc}-${l.item}`,
  })))), [loaded, selInvoices, descs]);
  const exWorks = r2(lines.reduce((s, l) => s + l.amount, 0));
  const gst = useMemo(() => (loaded ? loaded.gst : gstByRate(lines)), [loaded, lines]);
  const adv = useMemo(() => apportionAdvance(customer, selInvoices, scope, project), [customer, selInvoices, scope, project]);
  const onAccount = useMemo(() => onAccountFor(customer, project), [customer, project]);
  const net = r2(exWorks + gst.reduce((s, g) => s + g.amount, 0) + adjs.reduce((s, a) => s + (Number(a.amount) || 0) * (a.sign >= 0 ? 1 : -1), 0));

  const retentionPct = settings ? (settings.retention_pct ?? 5) : null;
  // auto lines are rebuilt only when the invoice selection / advance basis changes, so manual edits survive date or text changes
  useEffect(() => {
    if (loaded || retentionPct === null) return;
    setAdjs((prev) => [...autoAdjustments(selInvoices, adv, retentionPct, exWorks, onAccount), ...prev.filter((a) => a.kind === "other")]);
  }, [selInvoices, adv, exWorks, retentionPct, loaded, onAccount]);

  const search = async () => {
    if (!query.trim()) { toast.error(mode === "po" ? "Enter a PO number (wildcards allowed)" : "Enter a project number"); return; }
    setBusy(true); setLoaded(null);
    try {
      const { data } = await axios.post(`${API}/proforma/search`, { mode, value: query.trim() });
      setRes({ ...data, mode, query: query.trim() }); setDescs({});
      const counts = {};
      data.invoices.forEach((i) => { counts[i.customer] = (counts[i.customer] || 0) + 1; });
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
      setCust(top);
      setSelected(new Set(data.invoices.filter((i) => i.customer === top).map((i) => i.billing_doc)));
      const cleanPo = (p) => String(p).replace(/^\s*P\.?\s*O\b\.?\s*(NO\b\.?|NUMBER\b)?\s*[:.\-]*\s*/i, "");
      const pos = [...new Set(data.invoices.filter((i) => i.customer === top && i.po_no).map((i) => `${cleanPo(i.po_no)}${i.po_date ? ` DT. ${dmy(i.po_date)}` : ""}`))];
      setPi((p) => ({ ...p, id: undefined, number: undefined, po_text: pos.length ? `PO NO. :- ${pos.join(", ")}` : (mode === "po" ? `PO NO. :- ${query.trim()}` : ""),
        contact: p.contact || settings?.contact, phone: p.phone || settings?.phone, email: p.email || settings?.email, section_title: "" }));
      if (!data.invoices.length) toast.info("No invoices found");
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
    finally { setBusy(false); }
  };

  const pickCustomer = (code) => { setCust(code); setSelected(new Set((res?.invoices || []).filter((i) => i.customer === code).map((i) => i.billing_doc))); };
  const toggle = (doc) => setSelected((s) => { const n = new Set(s); n.has(doc) ? n.delete(doc) : n.add(doc); return n; });

  const buildBody = () => ({
    date: pi.date, mode: loaded?.mode || res?.mode || mode, query: loaded?.query || res?.query || query.trim(),
    customer: loaded?.customer || { code: customer?.code, name: customer?.name, address: customer?.address, gstin: customer?.gstin },
    delivery: loaded?.delivery || customer?.ship_to || {}, po_text: pi.po_text || "", contact: pi.contact || "", phone: pi.phone || "", email: pi.email || "",
    section_title: pi.section_title || "", lines: lines.map(({ _key, tax, ...l }) => l), gst,
    adjustments: adjs.map((a) => ({ ...a, amount: Number(a.amount) || 0 })), invoices: loaded?.invoices || selInvoices.map((i) => i.billing_doc),
  });

  const preview = async () => {
    if (!lines.length) { toast.error("Select at least one invoice"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/proforma/preview`, buildBody(), { params: pi.id ? { pid: pi.id } : {} });
      setPreviewUrl(r.data.pages);
    } catch { toast.error("Could not build the preview"); }
    finally { setBusy(false); }
  };
  const closePreview = () => setPreviewUrl(null);

  const save = async () => {
    if (!lines.length) { toast.error("Select at least one invoice"); return; }
    setBusy(true);
    try {
      const body = buildBody();
      const { data } = pi.id ? await axios.put(`${API}/proforma/${pi.id}`, body) : await axios.post(`${API}/proforma`, body);
      setPi((p) => ({ ...p, id: data.id, number: data.number }));
      setLoaded(data); setRefresh((x) => x + 1); closePreview();
      toast.success(`Proforma invoice ${data.number} saved`);
      await downloadPdf(data.id, data.number);
      loadSettings(pi.date);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the proforma invoice"); }
    finally { setBusy(false); }
  };

  const openSaved = async (id) => {
    try {
      const { data } = await axios.get(`${API}/proforma/${id}`);
      setLoaded(data); setAdjs(data.adjustments || []); setRes(null);
      setPi({ id: data.id, number: data.number, date: data.date, contact: data.contact, phone: data.phone, email: data.email, po_text: data.po_text, section_title: data.section_title });
      setTab("new");
    } catch { toast.error("Could not open the proforma invoice"); }
  };
  const newPi = () => { setLoaded(null); setRes(null); setAdjs([]); setPi({ date: today(), contact: settings?.contact, phone: settings?.phone, email: settings?.email }); };

  const setLineDesc = (i, v) => {
    if (loaded) setLoaded({ ...loaded, lines: loaded.lines.map((l, k) => (k === i ? { ...l, description: v } : l)) });
    else setDescs((d) => ({ ...d, [lines[i]._key]: v }));
  };
  const showEditor = loaded || selInvoices.length > 0;

  return (
    <div className="space-y-5" data-testid="proforma-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><FileText className="h-6 w-6 text-sky-700" />Proforma Invoice</h1>
          <p className="text-sm text-slate-500">Pull SAP invoices by project or customer PO, apportion the advance, less payments received, and issue a numbered PFI.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={tab === "new" ? "default" : "outline"} onClick={() => setTab("new")} data-testid="pi-tab-new"><FilePlus2 className="h-4 w-4 mr-1" />New / Edit</Button>
          <Button variant={tab === "history" ? "default" : "outline"} onClick={() => setTab("history")} data-testid="pi-tab-history"><History className="h-4 w-4 mr-1" />Generated PIs</Button>
          {isAdmin(user) && <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="pi-open-settings"><Settings2 className="h-4 w-4 mr-1" />Settings</Button>}
        </div>
      </div>

      {tab === "history" ? <PIHistory refreshKey={refresh} onOpen={openSaved} /> : (
        <>
          {!loaded && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="pi-search-bar">
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-48" data-testid="pi-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Project No. (WBS)</SelectItem>
                  <SelectItem value="po">Customer PO / Ref. No.</SelectItem>
                </SelectContent>
              </Select>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="w-72"
                placeholder={mode === "po" ? "e.g. 4600009710 or 46000097*" : "e.g. 2602"} data-testid="pi-query-input" />
              <Button onClick={search} disabled={busy} data-testid="pi-search-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}Fetch invoices</Button>
              {res?.customers?.length > 1 && (
                <Select value={cust} onValueChange={pickCustomer}>
                  <SelectTrigger className="w-80" data-testid="pi-customer-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{res.customers.map((c) => <SelectItem key={c.code} value={c.code}>{c.name} ({(res.invoices || []).filter((i) => i.customer === c.code).length})</SelectItem>)}</SelectContent>
                </Select>
              )}
              {customer && <span className="text-xs text-slate-400 ml-auto" data-testid="pi-data-source">{customer.outstanding?.source === "live" ? "Advances, payments & open/closed status: live from SAP" : `Advances & payments as of AR sync ${res?.ar_synced_at ? new Date(res.ar_synced_at).toLocaleString("en-IN") : ""}`}</span>}
            </div>
          )}
          {(res?.warnings || []).map((w, i) => (
            <p key={i} className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`pi-warning-${i}`}><AlertTriangle className="h-4 w-4 shrink-0" />{w}</p>
          ))}
          {!loaded && custInvoices.length > 0 && <PIInvoicePicker invoices={custInvoices} selected={selected} onToggle={toggle} onAll={(v) => setSelected(new Set(v ? custInvoices.map((i) => i.billing_doc) : []))} />}

          {showEditor && (
            <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4" data-testid="pi-editor">
              <PIHeader pi={pi} setPi={setPi} number={pi.number || `${settings?.next_number || "…"} (next)`} customer={loaded?.customer || customer} delivery={loaded?.delivery || customer?.ship_to} />
              <PILines lines={lines} sectionTitle={pi.section_title || ""} onSection={(v) => setPi({ ...pi, section_title: v })} onLine={setLineDesc} />
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                <Row label="TOTAL EX-WORKS VALUE" value={exWorks} bold testid="pi-exworks" />
                {gst.map((g) => <Row key={g.rate} label={g.label} value={g.amount} testid={`pi-gst-${g.rate}`} />)}
                {!loaded && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="pi-advance-info">
                    Advance basis:
                    <Select value={scope} onValueChange={setScope}>
                      <SelectTrigger className="h-7 w-44 text-xs bg-white" data-testid="pi-adv-scope"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="project">Project {project || "—"} advances</SelectItem><SelectItem value="all">All customer advances</SelectItem></SelectContent>
                    </Select>
                    <span>on hand <b>₹{inr(adv.onHand)}</b> ({adv.count}) × selected open basic ₹{inr(adv.selBasic)} ÷ customer open basic ₹{inr(adv.totalBasic)} = <b>{(adv.ratio * 100).toFixed(2)}%</b> → <b>₹{inr(adv.amount)}</b></span>
                  </div>
                )}
                <PIAdjustments adjustments={adjs} onChange={setAdjs} />
                <div className="flex items-center justify-between border-t-2 border-slate-800 pt-2 mt-2">
                  <span className="font-bold text-slate-900">TOTAL PAYABLE AGAINST PROFORMA INVOICE</span>
                  <span className="text-xl font-bold tabular-nums text-slate-900" data-testid="pi-net-payable">₹ {inr(net)}</span>
                </div>
                <p className="text-sm font-medium text-slate-600" data-testid="pi-net-words">{amountInWords(net)}</p>
              </div>
              <div className="flex justify-end gap-2">
                {loaded && <Button variant="outline" onClick={newPi} data-testid="pi-new-btn">Start a new PI</Button>}
                {pi.id && <Button variant="outline" onClick={() => downloadPdf(pi.id, pi.number).catch(() => toast.error("PDF download failed"))} data-testid="pi-download-btn"><Download className="h-4 w-4 mr-1" />Download PDF</Button>}
                {pi.id && <Button variant="outline" onClick={() => downloadXlsx(pi.id, pi.number).catch(() => toast.error("Excel download failed"))} data-testid="pi-download-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Download XLS</Button>}
                <Button onClick={preview} disabled={busy} className="bg-sky-700 hover:bg-sky-800" data-testid="pi-preview-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}Preview PDF
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      <PIPreviewDialog url={previewUrl} onClose={closePreview} onConfirm={save} busy={busy} isSaved={!!pi.id}
        onDownload={(ext) => downloadDraft(buildBody(), pi.id, ext).catch(() => toast.error("Download failed"))} />
      <PISettingsDialog open={showSettings} onClose={() => setShowSettings(false)} onSaved={setSettings} />
    </div>
  );
}

const Row = ({ label, value, bold, testid }) => (
  <div className="flex items-center justify-between text-sm" data-testid={testid}>
    <span className={bold ? "font-bold text-slate-900" : "text-slate-700"}>{label}</span>
    <span className={`tabular-nums ${bold ? "font-bold" : ""}`}>{inr(value)}</span>
  </div>
);
